import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';
import ColumnHeaderWithInfo from '@components/common/ColumnHeaderWithInfo';

import { IConsistencyCheckHistorySummary } from '@interfaces/cmModule/consistencyCheck';

import { EConsistencyParametersPathStatus } from '@enums/common';

import {
	DATA_MODEL_PATH_TOOLTIP,
	DECREASED_TOOL_TOOLTIP,
	EXTRA_TOOL_TOOLTIP,
	INCREASED_TOOL_TOOLTIP,
	MISSING_TOOL_TOOLTIP,
	OTHER_TOOL_TOOLTIP,
	TOTAL_TOOL_TOOLTIP,
} from './constants';

import styles from './ConsistencyCheckHistorySummary.module.scss';

interface IConsistencyCheckHistorySummaryProps {
	viewParameter: (data: IConsistencyCheckHistorySummary) => void;
	viewExtraParameter: (data: IConsistencyCheckHistorySummary) => void;
}

const columnHelper = createColumnHelper<IConsistencyCheckHistorySummary>();

export const getColumns = ({
	viewParameter,
	viewExtraParameter,
}: IConsistencyCheckHistorySummaryProps): ColumnDef<any>[] => [
	columnHelper.accessor('dataModelPath', {
		header: () => {
			return (
				<ColumnHeaderWithInfo
					title='Data model path'
					tooltip={DATA_MODEL_PATH_TOOLTIP}
				/>
			);
		},
		cell: ({ getValue }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell
					maxTextWidth={200}
					text={value}
				/>
			);
		},
		enableSorting: false,
	}),
	columnHelper.accessor('pathStatus', {
		header: () => {
			return (
				<ColumnHeaderWithInfo
					title='Path status'
					tooltip={
						<div className={styles.statusWrapper}>
							<div className={styles.status}>
								<div className={styles.statusTitle}>
									<span className={styles.statusColorGreen}/>
									<div>In both</div>
								</div>
								<div className={styles.statusDescription}>
									Present in both the config and the reference template, but with different parameters
								</div>
							</div>
							<div className={styles.status}>
								<div className={styles.statusTitle}>
									<span className={styles.statusColorRed}/>
									<div>Missing</div>
								</div>
								<div className={styles.statusDescription}>
									Present in the reference template, but missing from the config
								</div>
							</div>
							<div className={styles.status}>
								<div className={styles.statusTitle}>
									<span className={styles.statusColorBlue}/>
									<div>Extra</div>
								</div>
								<div className={styles.statusDescription}>
									Present in the config, but missing from the reference template
								</div>
							</div>
						</div>
					}
				/>
			);
		},
		cell: ({ getValue }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const { title, color } = getValue();

			return (
				<AdaptiveTableCell
					color={color}
					text={title}
				/>
			);
		},
		enableSorting: false,
	}),
	columnHelper.accessor('metadata.totalNumber', {
		header: () => {
			return (
				<ColumnHeaderWithInfo
					title='Total'
					tooltip={TOTAL_TOOL_TOOLTIP}
				/>
			);
		},
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const value = original?.metadata?.totalNumber || '-';

			return <AdaptiveTableCell text={value.toString()}/>;
		},
	}),
	columnHelper.accessor('metadata.increasedNumber', {
		header: () => {
			return (
				<ColumnHeaderWithInfo
					title='Increased'
					tooltip={INCREASED_TOOL_TOOLTIP}
				/>
			);
		},
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const value = original?.metadata?.increasedNumber || '-';

			return <AdaptiveTableCell text={value.toString()}/>;
		},
	}),
	columnHelper.accessor('metadata.decreasedNumber', {
		header: () => {
			return (
				<ColumnHeaderWithInfo
					title='Decreased'
					tooltip={DECREASED_TOOL_TOOLTIP}
				/>
			);
		},
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const value = original?.metadata?.decreasedNumber || '-';

			return <AdaptiveTableCell text={value.toString()}/>;
		},
	}),
	columnHelper.accessor('metadata.otherNumber', {
		header: () => {
			return (
				<ColumnHeaderWithInfo
					title='Changed'
					tooltip={OTHER_TOOL_TOOLTIP}
				/>
			);
		},
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const value = original?.metadata?.otherNumber || '-';

			return <AdaptiveTableCell text={value.toString()}/>;
		},
	}),
	columnHelper.accessor('metadata.missingNumber', {
		header: () => {
			return (
				<ColumnHeaderWithInfo
					title='Missing'
					tooltip={MISSING_TOOL_TOOLTIP}
				/>
			);
		},
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const value = original?.metadata?.missingNumber || '-';

			return <AdaptiveTableCell text={value.toString()}/>;
		},
	}),
	columnHelper.accessor('metadata.extraNumber', {
		header: () => {
			return (
				<ColumnHeaderWithInfo
					title='Extra'
					tooltip={EXTRA_TOOL_TOOLTIP}
				/>
			);
		},
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const value = original?.metadata?.extraNumber || '-';

			return <AdaptiveTableCell text={value.toString()}/>;
		},
	}),
	columnHelper.accessor('metadata', {
		header: 'Actions',
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckHistorySummary, any>) => {
			const statusName = original?.pathStatus?.name;
			const isView = statusName === EConsistencyParametersPathStatus.inBoth
				|| statusName === EConsistencyParametersPathStatus.extra;

			const viewHandler = () => {
				if (statusName === EConsistencyParametersPathStatus.inBoth) {
					viewParameter(original);
				} else if (statusName === EConsistencyParametersPathStatus.extra) {
					viewExtraParameter(original);
				}
			};

			return (
				<Actions
					isView={isView}
					viewHandler={viewHandler}
				/>
			);
		},
	}),
];
